import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, from } from 'rxjs';
import { switchMap, take, concatMap, map } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import * as fromGraphQl from './graphql';
import { ApolloService } from '../apollo.service';
import { File as FileEntity } from '../../entities/file/file';
import { Page } from '../../entities/page/page';
import { saveAs } from 'file-saver';

export interface FileUploadParams {
  file: File; // Browser File type
  ownerId: string;
  role?: string;
  batchId?: string;
  ownerType?: string;
}
/**
 * The groups service.
 */
@Injectable()
export class FilesService {
  constructor(
    private apolloService: ApolloService,
    private http: HttpClient,
    @Inject('env') private environment: any
  ) {
  }

  public getFilesPaged(variables: fromGraphQl.GetFilesPagesVariables): Observable<FileEntity[]> {
    return this.apolloService.query<fromGraphQl.GetFilesPagedResponse>(fromGraphQl.GET_FILES_PAGED_QUERY, variables)
      .pipe(
        take(1),
        switchMap( (result) => {
          return of( FileEntity.createFiles( result.data.filesPage.content ) );
        })
      );
  }

  public getFilesPagedWithPagination(variables: fromGraphQl.GetFilesPagesVariables): Observable<Page<FileEntity>> {
    return this.apolloService.query<fromGraphQl.GetFilesPagedResponse>(fromGraphQl.GET_FILES_PAGED_QUERY, variables)
      .pipe(
        take(1),
        map((result) => {
          const page = new Page<FileEntity>(result.data.filesPage);
          page.content = FileEntity.createFiles(result.data.filesPage.content || []);
          return page;
        })
      );
  }

  public getFiles(variables: fromGraphQl.GetFilesVariables): Observable<FileEntity[]> {
    return this.apolloService.query<fromGraphQl.GetFilesResponse>(fromGraphQl.GET_FILES_QUERY, variables)
      .pipe(
        take(1),
        switchMap( (result) => {
          return of( FileEntity.createFiles( result.data.files ) );
        })
      );
  }

  public uploadFile(params: FileUploadParams): Observable<any> {
    const { file, ownerId, role } = params;

    const extension = this.extractFileExtension(file.type);
    const uuid = uuidv4();
    const fileName = `${uuid}.${extension}`;

    const httpParams = new HttpParams({
      fromObject: {
        fileName: file.name
      }
    });

    let url = `${this.environment.fileApi}/file/upload/?ownerId=${ownerId}`;
    if (role) {
      url += `&role=${role}`;
    }
    if (params.batchId) {
      url += `&batchId=${params.batchId}`;
    }
    if (params.ownerType) {
      url += `&ownerType=${params.ownerType}`;
    }
    const formData = new FormData();
    formData.append('file', file, fileName);

    return this.http.post(url, formData, { params: httpParams });
  }

  public downloadFile(file: FileEntity): void {
    const downloadName = file?.fileName;
    const url = this.environment.fileApi + '/file/'+ file?.fullPath;
    this.http.get(url, { responseType: 'blob' }).subscribe((response: any) => {
      saveAs(response, downloadName);
    });
  }

  public deleteFile(file: FileEntity, callback: any): void {
    const id = file?.id;
    this.apolloService.mutateAndExecute(
      fromGraphQl.DELETE_FILE_MUTATION,
      {
        id
      },
      callback
    );
  }


  private extractFileExtension(mimeType: string): string {
    return mimeType.replace(/(.*)\//g, '');
  }

  // ========================================
  // V2 File Upload Methods
  // ========================================

  /**
   * Batch upload files: Creates all file records in one GraphQL call, then uploads file data sequentially
   */
  public uploadFilesV2Batch(
    files: File[],
    ownerId: string,
    role: string,
    batchId: string,
    ownerType?: string
  ): Observable<{ file: File; fileId: string; extension: string }> {
    // Generate file IDs and extract extensions for all files
    const fileMetadata = files.map(file => ({
      file,
      fileId: uuidv4(),
      extension: this.extractFileExtension(file.type)
    }));

    // Create all file records in one GraphQL mutation
    return this.createFileRecordsBatch(fileMetadata, ownerId, role, batchId, ownerType).pipe(
      switchMap(() => from(fileMetadata)),
      concatMap(metadata =>
        this.uploadFileData(metadata.file, metadata.fileId, metadata.extension).pipe(
          switchMap(() => of(metadata))
        )
      )
    );
  }

  /**
   * Step 1: Create all file records in database via single GraphQL mutation
   * Only sends: id, fileName, batchId, owners
   * Backend will analyze uploaded files and set: size, contentType, extension
   */
  private createFileRecordsBatch(
    fileMetadata: Array<{ file: File; fileId: string; extension: string }>,
    ownerId: string,
    role: string,
    batchId: string,
    ownerType?: string
  ): Observable<FileEntity[]> {
    const fileInputs: fromGraphQl.FileInputDto[] = fileMetadata.map(metadata => ({
      id: metadata.fileId,
      fileName: metadata.file.name,
      batchId,
      owners: [{
        ownerId,
        ownerType,
        role
      }]
    }));

    return this.apolloService.mutateObservable<fromGraphQl.CreateFilesResponse>(
      fromGraphQl.CREATE_FILES_MUTATION,
      { fileInputs }
    ).pipe(
      take(1),
      switchMap(result => {
        if (result.data.createFiles && result.data.createFiles.length > 0) {
          return of(result.data.createFiles.map(f => new FileEntity(f)));
        }
        throw new Error('Failed to create file records');
      })
    );
  }

  /**
   * Step 2: Upload file data to storage
   * File is renamed to: {fileId}.{extension}
   * Backend will analyze file and update the DB record
   */
  private uploadFileData(file: File, fileId: string, extension: string): Observable<any> {
    const renamedFileName = `${fileId}.${extension}`;
    const formData = new FormData();
    formData.append('file', file, renamedFileName);

    const url = `${this.environment.fileApi}/file/upload/internal`;

    return this.http.post(url, formData);
  }
}
